// ── Public referral link API ────────────────────────────────────────────────
//
// Backs /r/:code, the page a shared referral message opens.
//
// Anonymous, like the invoice route: the recipient has no account and no API
// key, and the code in the URL is the only credential. The lookup is
// progressive enhancement — the page already knows the code from its own path,
// so a failure here costs the inviter's name and the day count, never the
// invitation itself.

import { publicRequest } from "./account";

export type ReferralInvite = {
	/** The normalised code, e.g. "UABCD". */
	code: string;
	/** The inviter's first name, or "" when they have none on record. */
	inviter: string;
	/** Days the redeemer gets for applying the code. */
	days: number;
};

/** The shape the service issues for a share link: U + four letters. */
const REFERRAL_CODE_RE = /^U[A-Z]{4}$/;

/** True when `code` is the only thing a share link can carry. */
export function isReferralCode(code: string | undefined): code is string {
	return !!code && REFERRAL_CODE_RE.test(code.trim().toUpperCase());
}

/** The canonical form of a path segment, or "" when it is not a code. */
export function normaliseReferralCode(code: string | undefined): string {
	if (!isReferralCode(code)) return "";
	return code.trim().toUpperCase();
}

/**
 * Resolves a code to its invitation, or null when the service does not know it.
 *
 * Every failure is null rather than an error: a dead link, an offline phone and
 * a timeout all mean the same thing to this page — carry on with the code it
 * already has.
 */
export async function getReferralInvite(
	code: string
): Promise<ReferralInvite | null> {
	const normalised = normaliseReferralCode(code);
	if (!normalised) return null;

	try {
		const invite = await publicRequest<ReferralInvite>(
			`/pub/referral/${encodeURIComponent(normalised)}`
		);
		return invite ?? null;
	} catch {
		return null;
	}
}
